// Dados do set (gerados por npm run data:build) usados pelo cliente.
import kantoJson from "@gen/kanto.set.json";
import type { SetDef, UnitDef, Star } from "@shared/types";
import pokeNames from "./pokenames.json";
import i18nJson from "@gen/i18n.json";
import { lang, t } from "./i18n";
import itemsJson from "@gen/items.json";

export const set = kantoJson as unknown as SetDef;

const byId = new Map(set.units.map((u) => [u.id, u]));

export function unitDef(id: string): UnitDef | undefined {
  return byId.get(id);
}

/** Espécie mostrada para a linha na estrela (respeita o branch do Eevee). */
export function speciesFor(unitId: string, star: Star, branch?: string): number {
  const def = byId.get(unitId);
  if (!def) return Number(unitId.replace(/\D/g, "")) || 1;
  const v = def.stages[star - 1];
  if (v === "branch") return def.branches?.find((b) => b.id === branch)?.species ?? (def.stages[0] as number);
  return v as number;
}

export function unitName(unitId: string, star: Star = 1, branch?: string): string {
  return speciesName(speciesFor(unitId, star, branch)) ?? unitId;
}

// ------------------------------------------------- textos traduzidos
const I18N = i18nJson as unknown as Record<string, Record<string, string>>;
/** Texto de dados no idioma atual (cai para o português e depois para `fallback`). */
export function tr(key: string, fallback = key): string {
  return I18N[lang()]?.[key] ?? I18N["pt-BR"]?.[key] ?? fallback;
}

// nomes por espécie (PokéAPI; em português os nomes são os mesmos do inglês)
const names = pokeNames as Record<string, string>;
export function speciesName(id: number): string | undefined {
  return I18N[lang()]?.[`species.${id}`] ?? names[String(id)];
}

export function traitName(key: string): string {
  return tr(`trait.${key}.name`, set.traits[key]?.name ?? key);
}

export function traitDescRaw(key: string): string {
  return tr(`trait.${key}.desc`, set.traits[key]?.desc ?? "");
}

export function componentName(key: string): string {
  return tr(`item.${key}.name`, set.components[key]?.name ?? key);
}

export function abilityText(a: { key: string; name: string; desc: string }): { name: string; desc: string } {
  return { name: tr(`ability.${a.key}.name`, a.name), desc: tr(`ability.${a.key}.desc`, a.desc) };
}

export function badgeName(id: string, fallback = id): string {
  return tr(`badge.${id}.name`, fallback);
}
export function badgeOption(id: string, i: number, fallback = ""): string {
  return tr(`badge.${id}.opt${i}`, fallback);
}
export function boardName(id: string, fallback = id): string {
  return tr(`board.${id}.name`, fallback);
}
export function boardDesc(id: string, fallback = ""): string {
  return tr(`board.${id}.desc`, fallback);
}
export function packageName(id: string, fallback = id): string {
  return tr(`package.${id}`, fallback);
}
export function cardCategoryName(id: string, fallback = id): string {
  return tr(`cardcat.${id}`, fallback);
}
/** Nome e descrição de uma carta (as Energias usam o modelo com o nome do tipo). */
export function cardText(id: string, trait: string | null, value: number | null, fallback: { name: string; desc: string }): { name: string; desc: string } {
  const fill = (s: string) => s.replaceAll("{traitName}", trait ? traitName(trait) : "").replaceAll("{value}", value === null ? "?" : String(value));
  if (trait) return { name: fill(tr("card.energy.name", fallback.name)), desc: fill(tr("card.energy.desc", fallback.desc)) };
  return { name: tr(`card.${id}.name`, fallback.name), desc: fill(tr(`card.${id}.desc`, fallback.desc)) };
}

export const COST_COLORS = ["", "var(--bw-cost-1)", "var(--bw-cost-2)", "var(--bw-cost-3)", "var(--bw-cost-4)", "var(--bw-cost-5)"];

// ------------------------------------------------------------------ itens

export interface ItemInfo { id: string; name: string; sprite: string; recipe: [string, string] | null; special?: boolean; price: number; desc: string; effect: Record<string, unknown>; stats: Record<string, number> }
const items = (itemsJson as unknown as Record<string, ItemInfo[]>)[set.id] ?? [];
const itemByRecipe = new Map(items.filter((it) => it.recipe).map((it) => [[...it.recipe!].sort().join("+"), it]));
const itemById = new Map(items.map((it) => [it.id, it]));
/** ids de todos os componentes e itens completos do set (para pré-carregar ícones) */
export const allItemIds = [...Object.keys(set.components), ...items.map((it) => it.id)];

const STAT_KEYS = ["adPct", "ap", "asPct", "critPct", "armor", "mr", "hp", "startMana"] as const;
export function statsText(stats: Record<string, number>): string {
  return Object.entries(stats)
    .map(([k, v]) => ((STAT_KEYS as readonly string[]).includes(k) ? t(`stat.${k as (typeof STAT_KEYS)[number]}`, { v }) : `+${v} ${k}`))
    .join(" · ");
}

export function itemName(id: string): string {
  return tr(`item.${id}.name`, set.components[id]?.name ?? itemById.get(id)?.name ?? id);
}

export function itemIcon(id: string): string {
  return `/assets/items/${id}.png`;
}

export function itemFromComponents(a: string, b: string): ItemInfo | undefined {
  return itemByRecipe.get([a, b].sort().join("+"));
}

/** Os componentes de uma unidade, agrupados em pares como no combate (2 componentes = 1 item). */
export function unitItemSlots(all: string[]): { id: string; kind: "item" | "component"; parts: string[] }[] {
  const out: { id: string; kind: "item" | "component"; parts: string[] }[] = [];
  // itens prontos (do item pool) valem sozinhos; os componentes formam pares na ordem
  for (const id of all) if (!set.components[id] && itemById.has(id)) out.push({ id, kind: "item", parts: [id] });
  const components = all.filter((id) => set.components[id]);
  for (let i = 0; i < components.length; i += 2) {
    const a = components[i]!, b = components[i + 1];
    const item = b !== undefined ? itemFromComponents(a, b) : undefined;
    if (item) out.push({ id: item.id, kind: "item", parts: [a, b!] });
    else for (const c of b !== undefined ? [a, b] : [a]) out.push({ id: c, kind: "component", parts: [c] });
  }
  return out;
}

function fill(desc: string, params: Record<string, unknown>): string {
  return desc.replace(/\{(\w+)\}/g, (_, k: string) => {
    const v = params[k];
    return Array.isArray(v) ? v.join("/") : String(v ?? `{${k}}`);
  });
}

/** Descrição curta (texto) de um componente ou item, com os números. */
export function itemDesc(id: string): string {
  const comp = set.components[id];
  if (comp) return statsText(comp.stat);
  const it = itemById.get(id);
  return it ? fill(tr(`item.${id}.desc`, it.desc), it.effect) : "";
}

/** HTML do tooltip de um componente ou item completo. */
export function itemTooltip(id: string, esc: (s: string) => string): string {
  const comp = set.components[id];
  if (comp) {
    const combos = Object.keys(set.components)
      .map((other) => ({ other, item: itemFromComponents(id, other) }))
      .filter((x) => x.item);
    return `<div class="tip__head"><img src="${itemIcon(id)}" alt="" width="30" height="30"><b>${esc(componentName(id))}</b><span class="bw-dim">${t("tip.component")}</span></div>
      <p>${esc(statsText(comp.stat))}</p>
      <p class="bw-dim">${t("tip.combine")}</p>
      <ul class="tip__combos">${combos.map(({ other, item }) => `<li><img src="${itemIcon(other)}" alt="" width="20" height="20"> + ${esc(componentName(other))} → <img src="${itemIcon(item!.id)}" alt="" width="20" height="20"><b>${esc(itemName(item!.id))}</b></li>`).join("")}</ul>`;
  }
  const it = itemById.get(id);
  if (!it) return esc(id);
  return `<div class="tip__head"><img src="${itemIcon(id)}" alt="" width="30" height="30"><b>${esc(itemName(id))}</b><span class="bw-dim">${t(it.special ? "tip.special" : "tip.item")}</span></div>
    <p>${esc(fill(tr(`item.${id}.desc`, it.desc), it.effect))}</p>
    <p class="bw-dim">${esc(statsText(it.stats))}</p>
    ${it.recipe ? `<p class="tip__recipe"><img src="${itemIcon(it.recipe[0])}" alt="" width="20" height="20"> ${esc(componentName(it.recipe[0]))} + <img src="${itemIcon(it.recipe[1])}" alt="" width="20" height="20"> ${esc(componentName(it.recipe[1]))}</p>` : `<p class="bw-dim">${t("tip.specialHint")}</p>`}`;
}

/** Tooltip de sinergia: valores de todos os níveis, com o nível ativo em destaque. */
export function traitTooltip(key: string, count: number, esc: (s: string) => string): string {
  const def = set.traits[key];
  if (!def) return esc(key);
  let tier = -1;
  def.breakpoints.forEach((b, i) => { if (count >= b) tier = i; });
  const effect = def.effect as Record<string, unknown>;
  const desc = esc(traitDescRaw(key)).replace(/\{(\w+)\}/g, (_, k: string) => {
    const v = effect[k];
    if (!Array.isArray(v)) return esc(String(v ?? k));
    return v.map((x, i) => (i === tier ? `<mark>${esc(String(x))}</mark>` : `<span class="bw-dim">${esc(String(x))}</span>`)).join("/");
  });
  const bps = def.breakpoints.map((b, i) => `<span class="tip__bp ${i === tier ? "on" : count >= b ? "done" : ""}">${b}</span>`).join("");
  return `<div class="tip__head"><b>${esc(traitName(key))}</b><span class="bw-dim">${t("tip.onBoard", { n: count })}</span></div>
    <div class="tip__bps">${bps}</div><p>${desc}</p>`;
}

/** Tooltip de uma unidade da loja: custo, tipos, classe, alcance e habilidade. */
export function unitTooltip(unitId: string, esc: (s: string) => string, sprite: (species: number) => string, owned = 0): string {
  const def = byId.get(unitId);
  if (!def) return esc(unitId);
  const ab = abilityText(def.ability);
  const evo = def.stages
    .map((st) => (typeof st === "number" ? speciesName(st) ?? String(st) : t("tip.unit.branch")))
    .filter((x, i, a) => i === 0 || x !== a[i - 1]);
  return `<div class="tip__head">${sprite(speciesFor(unitId, 1))}<b>${esc(unitName(unitId, 1))}</b><span class="bw-dim">${t("tip.unit.cost", { n: def.cost })}</span></div>
    <p>${def.traits.map((k) => `<span class="bw-type" data-type="${k}">${esc(traitName(k))}</span>`).join(" ")}
      <span class="bw-dim small">· ${esc(traitName(def.class))} · ${t("match.range", { n: def.range })}</span></p>
    <p><b>${esc(ab.name)}</b> <span class="bw-dim">(${t("match.mana")} ${def.ability.mana[0]}/${def.ability.mana[1]})</span>: ${esc(ab.desc)}</p>
    <p class="bw-dim small">${t("tip.unit.evolves")} ${evo.map(esc).join(" → ")}</p>
    ${owned ? `<p class="tip-owned">${t("tip.unit.owned", { n: owned })}</p>` : ""}`;
}
